import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import { getModule } from '@jwp/ott-common/src/modules/container';
import EntitlementController from '@jwp/ott-common/src/controllers/EntitlementController';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';

import useEventCallback from './useEventCallback';

export default function usePlayableMedia(
  item: PlaylistItem,
  {
    streamingProtocol,
    shouldKeepPreviousData = false,
    trackHistory = true,
    onStopPlayback,
  }: { streamingProtocol?: 'dash' | 'hls'; shouldKeepPreviousData?: boolean; trackHistory?: boolean; onStopPlayback?: () => void } = {},
) {
  const entitlementController = getModule(EntitlementController);
  const { i18n } = useTranslation();
  const { language } = i18n;
  const { subscription, passes, user } = useAccountStore(({ subscription, passes, user }) => ({ subscription, passes, user }));

  const handleStopPlayback = useEventCallback(() => onStopPlayback?.());

  useEffect(() => {
    return () => {
      // make sure the watch progress is saved before the playback session is stopped
      handleStopPlayback();
      void entitlementController.stopPlaybackSession(item);
    };
  }, [entitlementController, item, handleStopPlayback]);

  const {
    data: { playableItem, startOffset = 0 } = {},
    isLoading,
    error,
  } = useQuery({
    staleTime: 0,
    gcTime: 0,
    queryKey: [
      'media-signed',
      item.mediaid,
      language,
      streamingProtocol,
      !!subscription,
      passes.map(({ offerId }) => offerId),
      item.mediaOffers?.map(({ id }) => id),
      user?.id,
    ],
    queryFn: () => entitlementController.startPlaybackSession(item, language, streamingProtocol, trackHistory),
    placeholderData: shouldKeepPreviousData ? keepPreviousData : undefined,
    retry: entitlementController.playbackRetryCount,
    retryDelay: 1000,
  });

  return {
    playableItem,
    startOffset,
    isLoading,
    error,
  };
}
